import Http from './axios-config';
import {ACTIVATION_CODE_LIMIT} from "../constants/paginationLimits";

class ActivationCodeService {
    constructor() {
        this.http = new Http();
    }

    async getActivationCodesList({ limit = ACTIVATION_CODE_LIMIT, offset = 0, query = '' }) {
        const search = query ? `&query=${encodeURIComponent(query)}` : '';
        return await this.http.get(`/clinical-admins/activation-codes?limit=${limit}&offset=${offset}${search}`);
    }

    async createActivationCode(model) {
        return await this.http.post('/clinical-admins/activation-codes', model);
    }

    async getActivationCode(id) {
        return await this.http.get(`/clinical-admins/activation-codes/${id}`);
    }

    async updateActivationCode(id, model) {
        return await this.http.put(`/clinical-admins/activation-codes/${id}`, model);
    }

    /**
     * Change a code's limit and/or switch it on or off (super admins and Admins; the server checks).
     * Only the changed fields go, with the code's updatedAt as the page loaded it: if someone changed
     * the code since, the server refuses (409) and nothing is written. The caller shows any error
     * itself (no toast), next to the form.
     *
     * @param {number} id
     * @param {{numberOfUses?: (number|null), isActive?: boolean, expectedUpdatedAt?: string}} changes
     */
    async updateActivationCodeLimits(id, changes) {
        return await this.http.patch(`/clinical-admins/activation-codes/${id}/limits`, changes, { silentErrors: true });
    }

    async deleteActivationCode(id) {
        return await this.http.delete(`/clinical-admins/activation-codes/${id}`);
    }

    async downloadActivationCodeEmailsCSV(id, code) {
        try {
            const response = await this.http.instance.get(
                `/clinical-admins/activation-codes/${id}/emails/csv`,
                {
                    responseType: 'blob',
                }
            );

            if (response.data instanceof Blob && response.data.type && response.data.type.includes('application/json')) {
                const text = await response.data.text();
                const errorData = JSON.parse(text);
                throw new Error(errorData.message || 'Failed to download emails CSV');
            }

            const contentDisposition = response.headers['content-disposition'];
            let filename = `${code}_emails.csv`;
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);

            return response;
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                const text = await error.response.data.text();
                try {
                    const errorData = JSON.parse(text);
                    throw new Error(errorData.message || 'Failed to download emails CSV');
                } catch {
                    throw new Error('Failed to download emails CSV');
                }
            }
            throw error;
        }
    }
}

export default new ActivationCodeService();